import { TransactionResponse } from '@ethersproject/abstract-provider';
import { ProgressData } from '@reservoir0x/relay-sdk';
import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Network, URLs } from '@manifoldxyz/js-ts-utils';
import { appsApi } from '@/api/apps';
import { ClaimStudioInstance, ScribeStudioInstance } from '@/api/models/studioInstance';
import { signatureApi } from '@/api/signature';
import { DEFAULT_STEP_STATE } from '@/common/constants';
import networkCheck from '@/common/networkCheck';
import { getSupportedRelayNetworks } from '@/common/networkUtils';
import { TELEMETRY_EVENT_MAP } from '@/common/telemetry';
import {
  StepConfig,
  TransactionStep,
  TransactionStepState,
  TxStepId,
  TxStepStatus,
} from '@/common/types';
import {
  fetchClaimableMerkleInfo,
  getExtensionAddress,
  getTelemetryInstanceState,
  InstanceState,
  parseMintedTokenIdsFromClaimReceiptWithLogs,
} from '@/common/util';
import { getTransactionErrorMessage } from '@/common/web3TransactionErrorHandling';
import RelayNetworkDropdown from '@/components/complete/relay/RelayNetworkDropdown';
import ClaimExtensionContract from '@/contracts/claimExtensionContract';
import { useMintRelayParameters } from '@/hooks/useRelayMint';
import { enableRelayMint, executeRelayTransaction, getMintRelayParameters } from '@/lib/relay';
import useMainStore, { StoreType } from '@/store/mainStore';

export function createMintStep(
  studioInstance: ClaimStudioInstance | ScribeStudioInstance,
  overrideConfig?: Partial<StepConfig>,
  overrideState?: Partial<TransactionStepState>,
): TransactionStep {
  return {
    state: {
      ...DEFAULT_STEP_STATE,
      descriptionByStatus: {
        [TxStepStatus.Unstarted]:
          'Your tokens will be minted to your wallet and payment will be confirmed',
        [TxStepStatus.Pending]: '',
        [TxStepStatus.Confirming]: 'Waiting for blockchain confirmation of mint...',
        [TxStepStatus.Confirmed]: 'Mint complete',
        [TxStepStatus.Failed]: 'Mint failed',
      },
      ...overrideState,
    },
    config: {
      id: TxStepId.Mint,
      name: 'Mint Token',

      primaryButtonText: 'Buy Now',
      SecondaryComponent: enableRelayMint(studioInstance)
        ? ({ updateStepState }) => {
            const connectedNetwork = useMainStore((state) => state.connectedNetwork);
            const { parameters } = useMintRelayParameters();

            const [expanded, setExpanded] = useState(false);

            if (!connectedNetwork || !studioInstance || !parameters) {
              return <Skeleton height={68} />;
            }

            return (
              <RelayNetworkDropdown
                availableNetworks={getSupportedRelayNetworks(
                  Network.isTestnet(studioInstance.publicData.network),
                )}
                selectedNetworkId={connectedNetwork}
                parameters={parameters}
                onSelect={() => {
                  updateStepState({
                    error: undefined,
                  });
                }}
                expanded={expanded}
                setExpanded={setExpanded}
                onError={(error) => {
                  updateStepState({
                    error: error,
                  });
                }}
              />
            );
          }
        : undefined,

      ...overrideConfig,
    },
    validateBefore: async (store) => {
      const walletAddress = store.walletAddress;
      if (!walletAddress) {
        return { error: 'No wallet address found, please try disconnecting and reconnecting' };
      }
      const contract = getClaimExtensionContract(studioInstance);
      if (!contract) {
        return { error: 'Contract not available. Please try again.' };
      }

      // Validate user has enough mints left
      const claimableQuantity = await getClaimableQuantity(store, contract);
      if (claimableQuantity !== undefined) {
        if (claimableQuantity === 0) {
          if (studioInstance.publicData.mintingRestriction === 'Allowlist') {
            // check if user is on the allowlist to show a different error message
            const merkleInfo = await appsApi.getMerkleTree(
              studioInstance.publicData.instanceAllowlist?.merkleTreeId as number,
              walletAddress,
            );
            if (merkleInfo.length === 0) {
              return { error: `You are not on the allowlist` };
            } else {
              return { error: `You have used up all of your allowlist mints` };
            }
          } else {
            return { error: `You don't have any mints left` };
          }
        }
        if (claimableQuantity < store.numberOfMints) {
          useMainStore.getState().setNumberOfMints(claimableQuantity);
          return {
            warning: `You have ${claimableQuantity} mints left, this will mint ${claimableQuantity}`,
          };
        }
      }

      // validations for relay minting
      const enableRelay = enableRelayMint(studioInstance);
      if (enableRelay) {
        // Check user balance
        const provider = window.ManifoldEthereumProvider.provider();
        if (!provider) {
          return {
            valid: false,
            error: 'No provider available, please try disconnecting and reconnecting',
          };
        }
        const chainId = window.ManifoldEthereumProvider.chainId();
        if (!chainId) {
          return {
            valid: false,
            error: 'No network found, please try disconnecting and reconnecting',
          };
        }

        const isTestnet = Network.isTestnet(studioInstance.publicData.network);
        const supportedRelayNetworks = getSupportedRelayNetworks(isTestnet);
        if (!supportedRelayNetworks.includes(chainId)) {
          return {
            valid: false,
            error:
              'You are on an unsupported network, please choose eligible network from the dropdown',
          };
        }
      }

      return {};
    },
    execute: async (store: StoreType, updateCallback) => {
      const walletAddress = store.walletAddress;
      const selectedDelegateAddress = store.selectedDelegateAddress;
      const redemptionCode = store.redemptionCode;
      const mintToAddress = store.mintToAddress;
      const numberOfMints = store.numberOfMints;
      const connectedNetwork = store.connectedNetwork;
      const analytics = store.analytics;

      if (!walletAddress) {
        throw new Error('Wallet address is undefined');
      }
      if (!studioInstance) {
        throw new Error('Studio instance is undefined');
      }

      /*
       * Handle relay minting
       * We only use relay minting if the user is not the mint network
       */
      if (
        enableRelayMint(studioInstance) &&
        connectedNetwork !== studioInstance.publicData.network
      ) {
        // Track the attempt of mint transaction
        analytics?.capture({
          event: TELEMETRY_EVENT_MAP.REDEEM_TX_ATTEMPT,
          properties: {
            tx_type: 'relay',
          },
        });

        if (!connectedNetwork) {
          throw new Error('No connected network found, please try disconnecting and reconnecting');
        }
        const isTestnet = Network.isTestnet(studioInstance.publicData.network);
        const supportedRelayNetworks = getSupportedRelayNetworks(isTestnet);
        if (!supportedRelayNetworks.includes(connectedNetwork)) {
          throw new Error(`Unsupported Relay network: ${connectedNetwork}`);
        }
        const parameters = await getMintRelayParameters({
          chainId: connectedNetwork,
          studioInstance: studioInstance,
          recipient: mintToAddress || walletAddress,
          numberOfMints,
          withSigner: true,
        });

        let claimTxHash: string | undefined;
        try {
          claimTxHash = await executeRelayTransaction(
            connectedNetwork,
            studioInstance.publicData.network,
            parameters,
            (progress: ProgressData) => {
              // Show on UI when the transaction is submitted and pending
              if (progress.steps.length === 1) {
                const firstStep = progress.steps[0];
                if (!firstStep.error) {
                  // mark that the claim tx step is Confirming
                  updateCallback({
                    status: TxStepStatus.Confirming,
                  });
                } else {
                  updateCallback({
                    status: TxStepStatus.Pending,
                    error: firstStep.error,
                  });
                }
              }
            },
          );
          // @dev: we use this for revenue locale attribution and tax purposes as an organization/company
          await appsApi.postTransaction(
            studioInstance.appId,
            parseInt(
              (studioInstance as ClaimStudioInstance).publicData?.claimIndex?.toString() ||
                studioInstance.id,
            ),
            claimTxHash,
          );
        } catch (error) {
          analytics?.capture({
            event: TELEMETRY_EVENT_MAP.REDEEM_TX_FAIL,
            properties: {
              tx_type: 'relay',
            },
          });
          throw new Error(getTransactionErrorMessage(error));
        }

        // Track successful transaction
        analytics?.capture({
          event: TELEMETRY_EVENT_MAP.REDEEM_TX_SUCCESS,
          properties: {
            tx_type: 'relay',
          },
        });
        store.setMintedTxHash(claimTxHash);

        return;
      }

      // Track the attempt of mint transaction
      analytics?.capture({
        event: TELEMETRY_EVENT_MAP.REDEEM_TX_ATTEMPT,
      });

      // Handle non-relay minting
      await networkCheck(studioInstance.publicData.network);

      const claimContract = new ClaimExtensionContract(
        studioInstance.publicData.network,
        getExtensionAddress(studioInstance),
        studioInstance.publicData.contract.contractAddress,
        parseInt(
          (studioInstance as ClaimStudioInstance).publicData?.claimIndex?.toString() ||
            studioInstance.id,
        ),
      );

      let claimTx: TransactionResponse | undefined;

      try {
        if (studioInstance.publicData.mintingRestriction === 'RedemptionCodes') {
          // If redemption code is required but not provided, throw an error
          if (!redemptionCode) {
            throw new Error('Redemption code is required for this mint');
          }

          const signatureRedeemResponses = await signatureApi.claimRedemptionSign({
            walletAddress,
            extensionAddress: getExtensionAddress(studioInstance),
            instanceId: parseInt(
              (studioInstance as ClaimStudioInstance).publicData?.claimIndex?.toString() ||
                studioInstance.id,
            ),
            mintCount: numberOfMints,
            redemptionCode,
          });

          if (signatureRedeemResponses.status === 400) {
            throw new Error('Claim signature not found');
          }
          if (signatureRedeemResponses.status === 406) {
            throw new Error('Claim signature expired');
          }
          if (signatureRedeemResponses.status === 500) {
            throw new Error('Claim signature server error');
          }

          claimTx = await claimContract.mintSignature(
            numberOfMints,
            signatureRedeemResponses.signature,
            signatureRedeemResponses.message,
            signatureRedeemResponses.nonce,
            signatureRedeemResponses.expiration,
            studioInstance.publicData.mintPrice,
            walletAddress,
            mintToAddress || walletAddress,
          );
        } else {
          let mintIndices: number[] = [];
          let merkleProofs: string[][] = [];
          if (
            studioInstance.publicData.mintingRestriction === 'Allowlist' &&
            studioInstance.publicData.instanceAllowlist?.merkleTreeId
          ) {
            const claimableMerkleInfo = await fetchClaimableMerkleInfo(
              studioInstance.publicData.instanceAllowlist.merkleTreeId as number,
              selectedDelegateAddress || walletAddress,
              claimContract,
            );
            mintIndices = claimableMerkleInfo.mintIndices;
            merkleProofs = claimableMerkleInfo.merkleProofs;
          }

          if (numberOfMints === 1) {
            /**
             * single minting
             */
            if (!mintToAddress) {
              // minting to self or using delegate.cash to mint to self
              claimTx = await claimContract.mint(
                walletAddress,
                mintIndices,
                merkleProofs?.[0] || [],
                studioInstance.publicData.mintPrice,
                selectedDelegateAddress || walletAddress,
              );
            } else {
              // gift minting to a friend
              claimTx = await claimContract.mintProxy(
                walletAddress,
                mintIndices.slice(0, 1),
                merkleProofs.slice(0, 1),
                studioInstance.publicData.mintPrice,
                mintToAddress,
                numberOfMints,
              );
            }
          } else {
            /**
             * batch minting
             */
            if (!mintToAddress) {
              // minting to self or using delegate.cash to mint to self
              claimTx = await claimContract.mintBatch(
                walletAddress,
                mintIndices.slice(0, numberOfMints),
                merkleProofs.slice(0, numberOfMints),
                studioInstance.publicData.mintPrice,
                selectedDelegateAddress || walletAddress,
                numberOfMints,
              );
            } else {
              // gift minting to a friend
              claimTx = await claimContract.mintProxy(
                walletAddress,
                mintIndices.slice(0, numberOfMints),
                merkleProofs.slice(0, numberOfMints),
                studioInstance.publicData.mintPrice,
                mintToAddress,
                numberOfMints,
              );
            }
          }
        }
      } catch (error) {
        // Track failed transaction
        analytics?.capture({
          event: TELEMETRY_EVENT_MAP.REDEEM_TX_FAIL,
        });
        throw new Error(getTransactionErrorMessage(error));
      }

      // mark that the claim tx step is Confirming
      updateCallback({
        status: TxStepStatus.Confirming,
      });

      if (claimTx) {
        // @dev: we use this for revenue locale attribution and tax purposes as an organization/company
        await appsApi.postTransaction(
          studioInstance.appId,
          parseInt(
            (studioInstance as ClaimStudioInstance).publicData?.claimIndex?.toString() ||
              studioInstance.id,
          ),
          claimTx.hash,
        );
        try {
          try {
            const receipt = await claimTx.wait(1);

            store.setMintedTxHash(receipt.transactionHash);
            // Track successful transaction
            analytics?.capture({
              event: TELEMETRY_EVENT_MAP.REDEEM_TX_SUCCESS,
            });

            // Extract the token ID and store it in the main store
            const tokenIds = parseMintedTokenIdsFromClaimReceiptWithLogs(
              receipt,
              studioInstance.publicData.contract.spec,
              walletAddress,
            );

            if (tokenIds.length > 0) {
              store.setMintedTokenIds(tokenIds);
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            // Handle speed-up case which is a false error in a sense
            if (
              error?.code === 'TRANSACTION_REPLACED' &&
              !error.cancelled &&
              error.replacement?.hash
            ) {
              updateCallback({
                etherscanLink: URLs.getEtherscanURLForTransactionHash(
                  studioInstance.publicData.network,
                  error.replacement.hash,
                ),
                txHash: error.replacement.hash,
              });
              // Wait for the replacement transaction
              const provider = window.ManifoldEthereumProvider.provider();
              if (!provider) throw new Error('No provider available');
              const replacementTx = await provider.getTransaction(error.replacement.hash);
              const replacementReceipt = await replacementTx.wait(1);
              store.setMintedTxHash(replacementReceipt.transactionHash);

              // Extract the token ID from the replacement transaction and store it in the main store
              const tokenIds = parseMintedTokenIdsFromClaimReceiptWithLogs(
                replacementReceipt,
                studioInstance.publicData.contract.spec,
                walletAddress,
              );

              if (tokenIds.length > 0) {
                store.setMintedTokenIds(tokenIds);
              }

              // Track successful transaction after speed-up
              analytics?.capture({
                event: TELEMETRY_EVENT_MAP.REDEEM_TX_SUCCESS,
              });
            } else {
              // Track failed transaction
              analytics?.capture({
                event: TELEMETRY_EVENT_MAP.REDEEM_TX_FAIL,
              });
              // otherwise just rethrow the error for getTransactionErrorMessage to handle
              throw error;
            }
          }
        } catch (error) {
          // Track failed transaction
          analytics?.capture({
            event: TELEMETRY_EVENT_MAP.REDEEM_TX_FAIL,
          });

          const claimData = store.claimData;
          if (claimData) {
            const instanceStatus = getTelemetryInstanceState(studioInstance, claimData);

            // during primary sale override the error message to be more specific if sold out
            if (instanceStatus === InstanceState.SoldOut) {
              throw new Error('Transaction cannot be completed, the token is sold out');
            }

            // during primary sale override the error message to be more specific if ended
            if (instanceStatus === InstanceState.Ended) {
              throw new Error('Transaction cannot be completed, the sale has ended');
            }
          }
          throw new Error(getTransactionErrorMessage(error));
        }
      }
    },
  };
}

function getClaimExtensionContract(
  studioInstance: ClaimStudioInstance | ScribeStudioInstance,
): ClaimExtensionContract | undefined {
  try {
    const extensionAddress = getExtensionAddress(studioInstance);
    return new ClaimExtensionContract(
      studioInstance.publicData.network,
      extensionAddress,
      studioInstance.publicData.contract.contractAddress,
      parseInt(
        (studioInstance as ClaimStudioInstance).publicData?.claimIndex?.toString() ||
          studioInstance.id,
      ),
    );
  } catch (error) {
    console.error('Failed to initialize contract:', error);
  }
}

async function getClaimableQuantity(
  store: StoreType,
  contract: ClaimExtensionContract,
): Promise<number | undefined> {
  if (!store.claimData || !contract) return undefined;

  const claim = store.claimData;
  const totalSupply = claim.totalMax || Infinity;
  const availableSupply = Math.max(0, totalSupply - claim.total);
  const studioInstance = store.studioInstance;
  const selectedDelegateAddress = store.selectedDelegateAddress;
  const walletAddress = store.walletAddress;
  const mintToAddress = store.mintToAddress;
  if (!studioInstance || !walletAddress) return undefined;
  let availableForWallet = Infinity;

  // Handle allowlist mints
  if (
    studioInstance.publicData.mintingRestriction === 'Allowlist' &&
    studioInstance.publicData.instanceAllowlist?.merkleTreeId
  ) {
    const claimableMerkleInfo = await fetchClaimableMerkleInfo(
      studioInstance.publicData.instanceAllowlist?.merkleTreeId as number,
      selectedDelegateAddress || walletAddress,
      contract,
    );
    availableForWallet = claimableMerkleInfo.mintIndices.length;
  } else if (claim.walletMax && claim.walletMax > 0) {
    const totalMinted = await contract.getTotalMints(mintToAddress || walletAddress);
    availableForWallet = Math.max(0, claim.walletMax - totalMinted);
  }

  return Math.min(availableSupply, availableForWallet);
}
